import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Search,  Store, Add } from '../../assets/icons';
import '../../assets/css/admin/vendors.css';
import apiConfig from '../../config/api_config';
import { adminApi } from '../../network/apis/admin';
import LoadingSpinner from '../../components/elements/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminVendors = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        category: 'all',
        location: 'all'
    });

    const [vendors, setVendors] = useState([]);

    useEffect(() => {
        fetchVendors();
    }, []);

    const fetchVendors = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiConfig.get('/vendors/');
            if (response?.data?.data) {
                setVendors(response.data.data);
            } else {
                setVendors([]);
            }
        } catch (err) {
            console.error('Failed to fetch vendors:', err);
            setError('Failed to load vendors');
            setVendors([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate stats dynamically from fetched vendors
    const stats = useMemo(() => {
        const totalVendors = vendors.length;
        const activeVendors = vendors.filter(v => v.is_verified === true).length;
        const pendingApproval = vendors.filter(v => v.is_verified === false).length;
        return { totalVendors, activeVendors, pendingApproval };
    }, [vendors]);

    // Filter vendors
    const filteredVendors = useMemo(() => {
        return vendors.filter(vendor => {
            const searchMatch = 
                vendor.store_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vendor.city?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const statusMatch = filters.status === 'all' || 
                (filters.status === 'active' && vendor.is_verified) ||
                (filters.status === 'pending' && !vendor.is_verified);
            
            const categoryMatch = filters.category === 'all' || 
                vendor.store_type?.toLowerCase().includes(filters.category);
            
            const locationMatch = filters.location === 'all' || 
                vendor.city?.toLowerCase().includes(filters.location);
            
            return searchMatch && statusMatch && categoryMatch && locationMatch;
        });
    }, [vendors, searchTerm, filters]);

    if (loading) {
        return (
            <div className="admin-vendors">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading vendors...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-vendors">
                <div className="error-state">
                    <p>{error}</p>
                    <Button text="Retry" onClick={fetchVendors} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-vendors">
            <div className="page-header">
                <div className="header-content">
                    <h1>Vendor Management</h1>
                    <p>Monitor and manage marketplace vendors</p>
                </div>
                <div className="header-actions">
                    <Link to="/admin/vendors/applications">
                        <Button
                            text="Review Applications"
                            variant="outline"
                        />
                    </Link>
                    <Link to="/admin/vendors/new">
                        <Button
                            icon={<img src={Add} alt="" />}
                            text="Add Vendor"
                            variant="primary"
                        />
                    </Link>
                </div>
            </div>

            <div className="stats-row">
                <div className="stat-card">
                    <div className="stat-content">
                        <h3>Total Vendors</h3>
                        <div className="stat-value">{stats.totalVendors}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-content">
                        <h3>Active Vendors</h3>
                        <div className="stat-value success">{stats.activeVendors}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-content">
                        <h3>Pending Approval</h3>
                        <div className="stat-value warning">{stats.pendingApproval}</div>
                    </div>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search vendors by name or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                    </select>

                    <select
                        value={filters.category}
                        onChange={(e) => setFilters({...filters, category: e.target.value})}
                    >
                        <option value="all">All Categories</option>
                        <option value="feed">Feed Suppliers</option>
                        <option value="equipment">Equipment</option>
                        <option value="medicine">Medicine</option>
                    </select>

                    <select
                        value={filters.location}
                        onChange={(e) => setFilters({...filters, location: e.target.value})}
                    >
                        <option value="all">All Locations</option>
                        <option value="lagos">Lagos</option>
                        <option value="abuja">Abuja</option>
                        <option value="ph">Port Harcourt</option>
                    </select>
                </div>
            </div>

            <div className="vendors-grid">
                {filteredVendors.length === 0 ? (
                    <div className="empty-state">
                        <p>No vendors found</p>
                    </div>
                ) : (
                    filteredVendors.map(vendor => (
                    <div key={vendor.id} className="vendor-card">
                        <div className="card-header">
                            <div className="vendor-logo">
                                {vendor.store_logo ? (
                                    <img src={vendor.store_logo} alt={vendor.store_name} />
                                ) : (
                                    <div className="placeholder-logo">
                                        <img src={Store} alt="" />
                                    </div>
                                )}
                            </div>
                            <div className="vendor-info">
                                <h3>{vendor.store_name}</h3>
                                <span className="vendor-id">VND{vendor.id}</span>
                            </div>
                            <span className={`status ${vendor.is_verified ? 'active' : 'pending'}`}>
                                {vendor.is_verified ? 'Verified' : 'Pending'}
                            </span>
                        </div>

                        <div className="vendor-meta">
                            <div className="meta-item">
                                <img src={Store} alt="" />
                                <span>{vendor.store_type}</span>
                            </div>
                            <div className="meta-item">
                                <span>{vendor.city}, {vendor.state}</span>
                            </div>
                        </div>

                        <div className="vendor-details">
                            <div className="detail-item">
                                <label>Business Reg #</label>
                                <span>{vendor.business_registration_number}</span>
                            </div>
                            {vendor.store_description && (
                                <div className="detail-item">
                                    <label>Description</label>
                                    <span className="description">{vendor.store_description.substring(0, 100)}...</span>
                                </div>
                            )}
                        </div>

                        <div className="verification-badges">
                            {vendor.business_certificate && (
                                <span className="badge">Business Verified</span>
                            )}
                            {vendor.tax_certificate && (
                                <span className="badge">Tax Registered</span>
                            )}
                            {vendor.identity_document && (
                                <span className="badge">Identity Verified</span>
                            )}
                        </div>

                        <div className="card-actions">
                            <Link to={`/admin/vendors/${vendor.id}`}>
                                <Button
                                    text="View Details"
                                    variant="outline"
                                />
                            </Link>
                            {!vendor.is_verified && (
                                <Button
                                    text="Approve"
                                    variant="primary"
                                    onClick={async () => {
                                        try {
                                            await adminApi.approveVendor(vendor.id);
                                            toast.success('Vendor approved');
                                            fetchVendors();
                                        } catch (err) {
                                            toast.error('Approval failed');
                                        }
                                    }}
                                />
                            )}
                            {vendor.is_verified && (
                                <Button
                                    text="Suspend"
                                    variant="error"
                                    onClick={async () => {
                                        try {
                                            await apiConfig.patch(`/vendors/${vendor.id}/`, { is_active: false });
                                            toast.success('Vendor suspended');
                                            fetchVendors();
                                        } catch (err) {
                                            toast.error('Suspend failed');
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )))}
            </div>
        </div>
    );
};

export default AdminVendors;